import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Search,
  FileCheck,
  AlertTriangle,
  TrendingUp,
  Shield,
  Clock,
  CheckCircle,
  FileText,
  Users,
  Calendar,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const GestaoFiscal = () => {
  const { t } = useLanguage();

  const methodology = [
    { step: 1, titleKey: 'gfdPage.method1', descKey: 'gfdPage.method1Desc', icon: <FileText className="h-6 w-6" /> },
    { step: 2, titleKey: 'gfdPage.method2', descKey: 'gfdPage.method2Desc', icon: <Search className="h-6 w-6" /> },
    { step: 3, titleKey: 'gfdPage.method3', descKey: 'gfdPage.method3Desc', icon: <FileCheck className="h-6 w-6" /> },
    { step: 4, titleKey: 'gfdPage.method4', descKey: 'gfdPage.method4Desc', icon: <Users className="h-6 w-6" /> },
    { step: 5, titleKey: 'gfdPage.method5', descKey: 'gfdPage.method5Desc', icon: <FileText className="h-6 w-6" /> },
    { step: 6, titleKey: 'gfdPage.method6', descKey: 'gfdPage.method6Desc', icon: <CheckCircle className="h-6 w-6" /> },
  ];

  const pillars = [
    { 
      titleKey: 'gfdPage.pillar1', 
      subtitleKey: 'gfdPage.pillar1Sub',
      descKey: 'gfdPage.pillar1Desc',
      icon: <Search className="h-8 w-8" />,
      color: 'emerald'
    },
    { 
      titleKey: 'gfdPage.pillar2', 
      subtitleKey: 'gfdPage.pillar2Sub',
      descKey: 'gfdPage.pillar2Desc',
      icon: <TrendingUp className="h-8 w-8" />,
      color: 'bronze'
    },
    { 
      titleKey: 'gfdPage.pillar3', 
      subtitleKey: 'gfdPage.pillar3Sub',
      descKey: 'gfdPage.pillar3Desc',
      icon: <Shield className="h-8 w-8" />,
      color: 'emerald'
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="gestao-fiscal-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('gfdPage.heroTag')}
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
                {t('gfdPage.heroTitle')}{' '}
                <span className="gold-sheen">{t('gfdPage.heroHighlight')}</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                {t('gfdPage.heroDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                  data-testid="gfd-cta-primary"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('gfdPage.ctaPrimary')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald/10 rounded-sm blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80"
                  alt="Tax analysis"
                  className="relative rounded-sm shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-emerald">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: 'R$ 1,2bi+', labelKey: 'gfdPage.statsCredits' },
              { number: '60', labelKey: 'gfdPage.statsMonths' },
              { number: '20+', labelKey: 'gfdPage.statsExperience' },
              { number: '60', labelKey: 'gfdPage.statsDays' },
            ].map((stat, index) => (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-serif font-bold text-white mb-2">
                  {stat.number}
                </p>
                <p className="text-stone-300 text-sm">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-24 md:py-32 bg-white" data-testid="gfd-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gfdPage.approachTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('gfdPage.approachTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('gfdPage.approachDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`p-8 rounded-sm ${
                  pillar.color === 'bronze' 
                    ? 'bg-bronze text-white' 
                    : 'bg-stone-50 border border-stone-200'
                }`}
                data-testid={`pillar-${index}`}
              >
                <div className={`w-16 h-16 rounded-sm flex items-center justify-center mb-6 ${
                  pillar.color === 'bronze' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-emerald/10 text-emerald'
                }`}>
                  {pillar.icon}
                </div>
                <h3 className={`text-2xl font-serif font-bold mb-2 ${
                  pillar.color === 'bronze' ? 'text-white' : 'text-stone-900'
                }`}>
                  {t(pillar.titleKey)}
                </h3>
                <p className={`text-sm font-medium mb-4 ${
                  pillar.color === 'bronze' ? 'text-white/80' : 'text-bronze'
                }`}>
                  {t(pillar.subtitleKey)}
                </p>
                <p className={pillar.color === 'bronze' ? 'text-white/80' : 'text-stone-600'}>
                  {t(pillar.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="gfd-methodology">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gfdPage.methodologyTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('gfdPage.methodologyTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methodology.map((step, index) => (
              <motion.div
                key={step.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-sm border border-stone-200 hover:shadow-md transition-shadow"
                data-testid={`methodology-step-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald">
                      {step.icon}
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-bronze text-white text-xs font-bold flex items-center justify-center">
                      {step.step}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-stone-600 text-sm">
                      {t(step.descKey)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Fee Model */}
      <section className="py-24 md:py-32 bg-emerald text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('gfdPage.feeTag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                {t('gfdPage.feeTitle')}
              </h2>
              <p className="text-stone-300 mb-8 leading-relaxed">
                {t('gfdPage.feeDesc')}
              </p>
              <div className="space-y-4">
                <div className="bg-white/10 p-4 rounded-sm">
                  <p className="font-semibold mb-1">{t('gfdPage.feeAssets')}</p>
                  <p className="text-2xl font-serif font-bold text-bronze">20%</p>
                  <p className="text-stone-300 text-sm">{t('gfdPage.feeAssetsDesc')}</p>
                </div>
                <div className="bg-white/10 p-4 rounded-sm">
                  <p className="font-semibold mb-1">{t('gfdPage.feeLiabilities')}</p>
                  <p className="text-2xl font-serif font-bold text-bronze">8%</p>
                  <p className="text-stone-300 text-sm">{t('gfdPage.feeLiabilitiesDesc')}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm">
                <h3 className="text-xl font-serif font-bold mb-6">{t('gfdPage.guaranteesTitle')}</h3>
                <ul className="space-y-4">
                  {[
                    { icon: <CheckCircle className="h-5 w-5" />, textKey: 'gfdPage.guarantee1' },
                    { icon: <TrendingUp className="h-5 w-5" />, textKey: 'gfdPage.guarantee2' },
                    { icon: <Calendar className="h-5 w-5" />, textKey: 'gfdPage.guarantee3' },
                    { icon: <Shield className="h-5 w-5" />, textKey: 'gfdPage.guarantee4' },
                  ].map((item) => (
                    <li key={item.textKey} className="flex items-center gap-3">
                      <span className="text-bronze">{item.icon}</span>
                      <span className="text-stone-200">{t(item.textKey)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-24 md:py-32 bg-white" data-testid="gfd-security">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gfdPage.securityTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('gfdPage.securityTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: <Lock className="h-8 w-8" />, 
                titleKey: 'gfdPage.lgpd', 
                descKey: 'gfdPage.lgpdDesc' 
              },
              { 
                icon: <Shield className="h-8 w-8" />, 
                titleKey: 'gfdPage.insurance', 
                descKey: 'gfdPage.insuranceDesc' 
              },
              { 
                icon: <FileText className="h-8 w-8" />, 
                titleKey: 'gfdPage.foundation', 
                descKey: 'gfdPage.foundationDesc' 
              },
            ].map((item, index) => (
              <motion.div
                key={item.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-stone-50 rounded-sm"
                data-testid={`security-item-${index}`}
              >
                <div className="w-16 h-16 rounded-sm bg-emerald/10 flex items-center justify-center mx-auto mb-6 text-emerald">
                  {item.icon}
                </div>
                <h3 className="text-xl font-serif font-semibold text-stone-900 mb-3">
                  {t(item.titleKey)}
                </h3>
                <p className="text-stone-600">
                  {t(item.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Urgency */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <AlertTriangle className="h-16 w-16 text-bronze mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('gfdPage.urgencyTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('gfdPage.urgencyDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="gfd-urgency-cta"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('gfdPage.urgencyBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GestaoFiscal;
